import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * frontend/src/components/Pager.jsx
 *
 * One pager for all seven list pages, so they count and phrase things the same
 * way. Each page having its own was how "showing 20 of 150" ended up meaning
 * something different on every screen.
 *
 * It renders nothing when everything fits on one page — a pager under a list
 * of four is noise.
 */
export default function Pager({ total, limit, offset, onChange, noun = 'items' }) {
  if (!total || !limit || total <= limit) return null;

  const from = offset + 1;
  const to   = Math.min(offset + limit, total);
  const page = Math.floor(offset / limit) + 1;
  const pages = Math.ceil(total / limit);

  const go = (newOffset) => onChange(Math.max(0, Math.min(newOffset, (pages - 1) * limit)));

  return (
    <div className="flex items-center justify-between gap-4 py-3 text-sm flex-wrap">
      <span style={{ color: 'var(--text-secondary)' }}>
        Showing <strong style={{ color: 'var(--text)' }}>{from}–{to}</strong> of{' '}
        <strong style={{ color: 'var(--text)' }}>{total}</strong> {noun}
      </span>

      <div className="flex items-center gap-2">
        <button onClick={() => go(offset - limit)} disabled={offset === 0}
          className="px-2.5 py-1.5 rounded-lg border flex items-center gap-1 disabled:opacity-40"
          style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}>
          <ChevronLeft size={14} /> Previous
        </button>

        <span className="px-2" style={{ color: 'var(--text-secondary)' }}>
          Page {page} of {pages}
        </span>

        <button onClick={() => go(offset + limit)} disabled={to >= total}
          className="px-2.5 py-1.5 rounded-lg border flex items-center gap-1 disabled:opacity-40"
          style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}>
          Next <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}

/**
 * The state every list page needs, so none of them has to remember that a
 * filter change must reset the offset — forgetting that leaves you on page 4
 * of a result that now has one page, looking at nothing.
 */
export function usePaging(resetKey) {
  const [offset, setOffset] = useState(0);
  const [total, setTotal]   = useState(0);
  const [limit, setLimit]   = useState(100);

  useEffect(() => { setOffset(0); }, [resetKey]);

  /** Reads the headers the backend sets. */
  const readHeaders = (res) => {
    const t = Number(res?.headers?.['x-total-count']);
    const l = Number(res?.headers?.['x-page-limit']);
    if (Number.isFinite(t)) setTotal(t);
    if (Number.isFinite(l) && l > 0) setLimit(l);
  };

  return { offset, setOffset, total, limit, readHeaders };
}
